import React, { useEffect, useRef, useState } from "react";
import { Camera, Lightbulb } from "lucide-react";

const R2 = "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev";

// Signature-Shot + BTS-Setup vom Dominik-Kettner-Interview (gegradet, fertig)
const SIGNATURE = `${R2}/images/interview-bts-1.webp`; // Dominik im Sessel vorm Kunstwerk
const BTS_SETUP = `${R2}/images/interview-bts-2.webp`; // Atomos-Monitoring / Cinema-Setup

const EQUIPMENT = [
  "Sony Cinema-Line",
  "Atomos Shogun Inferno",
  "ProRes RAW HQ",
  "Aputure / Nanlite Licht",
];

const Lichtspiel = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (setVisible(true), io.disconnect())),
      { rootMargin: "-80px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const reveal = (delay) => ({
    transition: "opacity 0.7s ease-out, transform 0.7s ease-out",
    transitionDelay: delay,
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(24px)",
  });

  return (
    <section
      ref={ref}
      className="py-24 bg-background border-y border-white/5"
      data-testid="lichtspiel-section"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header — editorial */}
        <div className="mb-12 max-w-3xl" style={reveal("0s")}>
          <div className="inline-flex items-center gap-3 mb-5 text-xs font-semibold uppercase tracking-[0.28em] text-[hsl(var(--accent))]">
            <Camera className="w-4 h-4" />
            <span className="w-10 h-px bg-[hsl(var(--accent))]/60" />
            Behind the Lighting
          </div>
          <h2 className="text-4xl sm:text-6xl font-bold leading-[1.03] tracking-tight">
            Licht macht die <span className="text-[hsl(var(--accent))]">Stimmung</span>.
          </h2>
          <p className="text-lg text-muted-foreground mt-5 max-w-xl">
            Bevor die Kamera läuft, wird das Set ausgeleuchtet wie eine Filmszene.
            Hier: das Interview-Setup mit Cinema-Kamera, Atomos-Monitoring und
            gesetztem Licht.
          </p>
        </div>

        {/* Showcase */}
        <div className="grid lg:grid-cols-2 gap-5 lg:gap-6 items-stretch">
          {/* Signature Shot — Dominik im Sessel, groß */}
          <div
            className="group relative overflow-hidden rounded-2xl border border-white/10 bg-secondary/40"
            style={reveal("0.1s")}
          >
            <div className="aspect-[4/5] lg:aspect-auto lg:h-full">
              {visible && (
                <img
                  src={SIGNATURE}
                  alt="Interview-Setup — Talent im Ledersessel vor Kunstwerk, cinematisch ausgeleuchtet"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
              )}
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
            <div className="absolute left-5 bottom-5 right-5">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-md border border-white/15 text-xs font-medium text-white">
                <Lightbulb className="w-3.5 h-3.5 text-[hsl(var(--accent))]" />
                Interview-Set · Villingen-Schwenningen
              </div>
            </div>
          </div>

          {/* Rechts: BTS-Setup + Text + Equipment */}
          <div className="flex flex-col gap-5 lg:gap-6">
            {/* BTS-Setup Foto */}
            <div
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-secondary/40 flex-1"
              style={reveal("0.2s")}
            >
              <div className="aspect-[16/10] h-full">
                {visible && (
                  <img
                    src={BTS_SETUP}
                    alt="Cinema-Setup mit Atomos Shogun Inferno und Sony Kamera am Interview-Set"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                )}
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
              <div className="absolute left-4 bottom-4">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-md border border-white/15 text-xs font-medium text-white">
                  <Camera className="w-3.5 h-3.5 text-[hsl(var(--accent))]" />
                  Atomos Shogun · ProRes RAW HQ
                </span>
              </div>
            </div>

            {/* Text + Equipment */}
            <div
              className="rounded-2xl border border-white/10 bg-secondary/30 p-6 sm:p-7"
              style={reveal("0.3s")}
            >
              <h3 className="text-xl font-bold mb-2">Cinema-Setup am Interview-Set</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                Vom Lichtaufbau bis zum Atomos-Monitor: Jedes Setup wird geplant wie
                eine Spielfilmszene. Dieses Interview-Set haben wir gemeinsam mit
                unserer Partneragentur{" "}
                <a
                  href="https://www.zumblum.de"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[hsl(var(--accent))] font-medium hover:underline"
                >
                  Zumblum
                </a>{" "}
                aufgebaut.
              </p>
              <div className="flex flex-wrap gap-2">
                {EQUIPMENT.map((item) => (
                  <span
                    key={item}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/5 border border-white/10 text-white/80"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Lichtspiel;
